import React, { createContext, useState, useEffect, useContext } from 'react';
import authService from '../services/authService';

// Crear el contexto
const AuthContext = createContext(null);

// Proveedor del contexto
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Verificar autenticación al cargar la aplicación
  useEffect(() => {
    const checkAuth = async () => {
      try {
        setLoading(true);
        console.log('Verificando token...');
        const isValid = await authService.verifyToken();
        console.log('Token válido:', isValid);
        
        if (isValid) {
          // Obtener información del usuario
          console.log('Obteniendo información del usuario desde AuthContext...');
          const userInfo = await authService.fetchUserInfo();
          console.log('Información del usuario obtenida:', userInfo);
          if (userInfo) {
            console.log('Estableciendo usuario en el contexto:', userInfo);
            setUser(userInfo);
          } else {
            // Si no se pudo obtener la información, usar la almacenada en localStorage
            console.log('No se pudo obtener información del usuario, usando localStorage...');
            const storedUserInfo = authService.getUserInfo();
            console.log('Información del usuario desde localStorage:', storedUserInfo);
            setUser(storedUserInfo || { isAuthenticated: true });
          }
        } else {
          setUser(null);
        }
      } catch (err) {
        console.error('Error en checkAuth:', err);
        setError(err.message);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Función de login
  const login = async (username, password) => {
    try {
      setLoading(true);
      setError(null);
      
      await authService.login(username, password);
      
      // Obtener información del usuario
      const userInfo = authService.getUserInfo();
      setUser(userInfo || { isAuthenticated: true });
      
      return true;
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al iniciar sesión');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Función de logout
  const logout = () => {
    authService.logout();
    setUser(null);
  };

  // Verificar si el usuario tiene un rol específico
  const hasRole = (roleName) => {
    if (!user || !user.roles) {
      console.log(`[AuthContext] hasRole(${roleName}): No hay usuario o roles definidos`, user);
      return false;
    }
    // Comprobar tanto role.name como role.role_name para mayor compatibilidad
    const hasTheRole = user.roles.some(role => {
      console.log(`[AuthContext] Comparando rol: ${JSON.stringify(role)} con ${roleName}`);
      return role.name === roleName || role.role_name === roleName;
    });
    console.log(`[AuthContext] hasRole(${roleName}) = ${hasTheRole}`);
    return hasTheRole;
  };

  // Valor del contexto
  const value = {
    user,
    loading,
    error,
    login,
    logout,
    hasRole,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Hook personalizado para usar el contexto
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};

export default AuthContext; 